import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  TextArea,
  Card,
  Callout,
  Badge,
  Separator,
} from '@radix-ui/themes';
import {
  MagicWandIcon,
  InfoCircledIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { useGenerateSignalMutation } from '../../graphql/mutations/GenerateSignal.generated';
import { GeneratedSignalResult } from '../../types';

export interface AISignalPromptProps {
  onGenerated: (result: GeneratedSignalResult) => void;
  onSwitchToManual: () => void;
}

const AISignalPrompt: React.FC<AISignalPromptProps> = ({
  onGenerated,
  onSwitchToManual,
}) => {
  const [description, setDescription] = useState('');
  const [generateSignal, { loading, error }] = useGenerateSignalMutation();
  const [lastResult, setLastResult] = useState<GeneratedSignalResult | null>(null);

  const handleGenerate = async () => {
    if (!description.trim()) return;

    try {
      const result = await generateSignal({
        variables: {
          input: { description: description.trim() },
        },
      });

      if (result.data?.generateSignal) {
        const generated = result.data.generateSignal as GeneratedSignalResult;
        setLastResult(generated);
        onGenerated(generated);
      }
    } catch {
      // Error is handled by Apollo and displayed below
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'green';
    if (confidence >= 0.6) return 'yellow';
    return 'orange';
  };

  return (
    <Box>
      <Card size="2">
        <Flex direction="column" gap="4">
          <Box>
            <Flex align="center" gap="2" mb="2">
              <MagicWandIcon width={18} height={18} />
              <Text size="3" weight="medium">
                Describe your signal
              </Text>
            </Flex>
            <Text size="2" color="gray" mb="3">
              Tell AI what you want to monitor and it will generate the signal
              configuration for you.
            </Text>
          </Box>

          <TextArea
            placeholder="Example: Alert me when any transaction sends more than 100 ETH to 0x742d35Cc6634C0532925a3b844Bc454e4438f44e"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={4}
            style={{ resize: 'vertical' }}
          />

          <Callout.Root color="blue" size="1">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              Be specific about addresses, amounts, and event types. The AI
              works best with concrete values rather than placeholders.
            </Callout.Text>
          </Callout.Root>

          {error && (
            <Callout.Root color="red" size="1">
              <Callout.Text>
                {error.message || 'Failed to generate signal. Please try again.'}
              </Callout.Text>
            </Callout.Root>
          )}

          {lastResult && (
            <>
              <Separator size="4" />
              <Box>
                <Flex align="center" gap="2" mb="2">
                  <Text size="2" weight="medium">
                    AI Confidence
                  </Text>
                  <Badge color={getConfidenceColor(lastResult.confidence)}>
                    {Math.round(lastResult.confidence * 100)}%
                  </Badge>
                </Flex>
                <Text size="2" color="gray">
                  {lastResult.reasoning}
                </Text>
              </Box>
            </>
          )}

          <Flex gap="3" justify="between" align="center">
            <Button variant="ghost" color="gray" onClick={onSwitchToManual}>
              Create manually instead
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={loading || !description.trim()}
            >
              {loading ? (
                <>
                  <ReloadIcon className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <MagicWandIcon />
                  Generate Signal
                </>
              )}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Box>
  );
};

export default AISignalPrompt;
